import { Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  ShieldCheck,
  MapPin,
  Coins,
  BadgeCheck,
  Timer,
  Banknote,
  Award,
  CheckCircle2,
  Landmark,
  Handshake,
} from "lucide-react";
import { BRAND, IMAGES, telLink, telLinkPrimary, waLink } from "@/lib/brand";
import BrandStamp from "@/components/BrandStamp";
import usePageMeta from "@/hooks/usePageMeta";

const PhoneStrip = () => (
  <div className="bg-[#641C24] text-[#FFFDF8] border-y border-[#D4AF37]/40">
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="inline-flex items-center justify-center w-9 h-9 bg-[#D4AF37] text-[#2D181A]">
          <Phone size={16} strokeWidth={2.2} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Best Rate — Call Now · अभी कॉल करें
          </div>
          <a
            href={telLinkPrimary}
            data-testid="gcr-phone-strip"
            className="font-display text-2xl sm:text-3xl font-black tracking-tight text-[#FFFDF8] hover:text-[#D4AF37] transition-colors"
          >
            {BRAND.phonePrimary}
          </a>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a
          href={waLink(
            "Namaste! I'm from Golf Course Road / DLF area. Please share today's best gold rate. (Top Rate)"
          )}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FBA57] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider"
        >
          <MessageCircle size={15} /> WhatsApp
        </a>
        <a
          href={telLinkPrimary}
          className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8860B] text-[#2D181A] px-5 py-2.5 text-xs font-semibold uppercase tracking-wider"
        >
          <Phone size={15} /> Call
        </a>
      </div>
    </div>
  </div>
);

const NEARBY = [
  "Golf Course Road",
  "Golf Course Extension",
  "DLF Phase 1",
  "DLF Phase 2",
  "DLF Phase 3",
  "DLF Phase 4",
  "DLF Phase 5",
  "Sushant Lok 1",
  "Sushant Lok 2",
  "Sector 42",
  "Sector 43",
  "Sector 53",
  "Sector 54",
  "Sector 55–56",
  "M3M Golf Estate",
  "Ambience Island",
  "Nirvana Country",
  "South City",
];

const REASONS = [
  {
    icon: Award,
    title: "Decades of Experience",
    body: "Generations of gold expertise. Two branches in Gurgaon, thousands of satisfied families across Golf Course Road, DLF and Sushant Lok.",
  },
  {
    icon: BadgeCheck,
    title: "Govt-Approved Gold Valuers",
    body: "Officially licensed valuers with certificates accepted by banks, insurance companies and courts.",
  },
  {
    icon: Timer,
    title: "Spot Cash Today",
    body: "Walk in with your gold — walk out with spot cash. Payout in under 30 minutes.",
  },
  {
    icon: Banknote,
    title: "Best Price, No Manipulation",
    body: "Live karat testing, calibrated weighing, transparent formulas. Everything shown on your side of the counter.",
  },
  {
    icon: Landmark,
    title: "Bank Mortgage Buyback",
    body: "Gold pledged with a bank? We settle directly with the lender at the best market rate.",
  },
  {
    icon: Handshake,
    title: "Most Trustworthy Dealer",
    body: "Real reviews, repeat customers, discreet handling. The gold buyer families in South Gurgaon actually recommend.",
  },
];

const FAQ = [
  {
    q: "Where is the best cash-for-gold dealer near Golf Course Road, Gurgaon?",
    a: `Top Rate is a leading cash-for-gold dealer with two Gurgaon branches — Shop No. 230, Jacubpura (near Chota Gurudwara) and Shop 256, Sapphire Mall, Sector 49 (walking distance from Golf Course Road / DLF Phase 5). Call ${BRAND.phonePrimary} for today's best rate.`,
  },
  {
    q: "Do you provide pickup / home visits in Golf Course Road area?",
    a: "Yes. For senior citizens, larger consignments or if you prefer privacy, we send a certified valuer to your home across DLF Phase 1–5, Sushant Lok, Golf Course Road, Sector 42–56, M3M Golf Estate, Ambience Island, Nirvana Country and adjacent premium societies. Prior appointment required.",
  },
  {
    q: "Are you govt-approved gold valuers?",
    a: "Yes — Top Rate operates as govt-approved gold valuers. Our valuation certificates are accepted by banks, insurance companies and courts across Delhi NCR. This means every transaction is legally clean and verifiable.",
  },
  {
    q: "How do I know I'm getting the best price?",
    a: "Everything is done in front of you: XRF karat testing (non-destructive), digital calibrated weighing, and the payout formula shown on paper. We benchmark daily against MCX + IBJA rates. Compare our quote with any other dealer — call " + BRAND.phonePrimary + " for a same-day quote.",
  },
  {
    q: "Can I get spot cash today?",
    a: "Yes. Cash upto ₹2 lakh is paid on-spot at the branch. Larger amounts are transferred instantly via NEFT / RTGS / IMPS. Bring a photo ID; PAN required above ₹2 lakh.",
  },
];

export default function GolfCourseRoadPage() {
  usePageMeta({
    title:
      "Best Cash for Gold Dealer in Golf Course Road, Gurgaon IT Hub | Top Rate — Spot Cash Today",
    description:
      "Top Rate — the most trustworthy cash-for-gold dealer near Golf Course Road & Gurgaon IT Hub. Govt-approved gold valuers, decades of experience. Best price, spot cash, instant liquidity. Call " +
      BRAND.phonePrimary +
      ".",
    keywords:
      "cash for gold Golf Course Road Gurgaon, gold buyer Gurgaon IT Hub, gold loan Gurgaon, best rate for gold DLF, sell gold DLF Gurgaon, cash for gold Sushant Lok, gold dealer M3M Golf Estate, sell gold Sector 42 43 54 Gurgaon, govt approved gold valuer Gurgaon, spot cash gold Gurgaon, gold buyer near Golf Course Road, gold investment India",
    path: "/cash-for-gold-golf-course-road-gurgaon",
  });

  return (
    <div>
      <PhoneStrip />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#FAF6EC]">
        <img
          src={IMAGES.hero}
          alt=""
          aria-hidden="true"
          className="absolute inset-y-0 right-0 h-full w-1/2 object-cover opacity-25 hidden md:block"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(255,253,248,1) 0%, rgba(255,253,248,0.95) 45%, rgba(255,253,248,0.35) 75%, rgba(255,253,248,0) 100%)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7 tr-fade-up">
              <div className="inline-flex items-center gap-2 border border-[#641C24]/40 bg-[#FFFDF8] text-[#641C24] px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold">
                <MapPin size={13} strokeWidth={2} />
                Golf Course Road · Gurgaon IT Hub · DLF · Sushant Lok
              </div>

              <h1 className="font-display text-[#2D181A] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.0] mt-6">
                Best Cash for Gold Dealer{" "}
                <span className="italic text-[#B8860B]">
                  near Golf Course Road, Gurgaon
                </span>
                .
              </h1>

              <p className="mt-6 text-base sm:text-lg text-[#4A3B3C] leading-relaxed max-w-2xl">
                Just call for today's best price, best rate and spot cash.{" "}
                <span className="text-[#641C24] font-semibold">
                  Top Rate
                </span>{" "}
                is the most trustworthy gold dealer in South Gurgaon —
                govt-approved valuers with decades of experience.
              </p>

              {/* PRIMARY PHONE - BIG */}
              <a
                href={telLinkPrimary}
                data-testid="gcr-hero-phone"
                className="mt-6 inline-flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-5 bg-[#641C24] text-[#FFFDF8] pl-5 pr-6 py-4 sm:py-5 shadow-[0_10px_30px_-10px_rgba(100,28,36,0.55)] hover:bg-[#4A151A] transition-colors w-full sm:w-auto"
              >
                <span className="inline-flex items-center gap-2.5">
                  <span className="inline-flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 bg-[#D4AF37] text-[#2D181A] shrink-0">
                    <Phone size={18} strokeWidth={2.4} />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">
                    Call For Price · अभी कॉल करें
                  </span>
                </span>
                <span className="font-display font-black tracking-tight text-3xl sm:text-4xl md:text-5xl leading-none text-[#FFFDF8] tabular-nums">
                  {BRAND.phonePrimary}
                </span>
              </a>

              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <a
                  href={waLink(
                    "Namaste! I'm from Golf Course Road / DLF area. Please share today's best gold rate. (Top Rate)"
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-[#641C24]/60 hover:bg-[#641C24] hover:text-[#FFFDF8] text-[#641C24] px-6 py-3.5 text-sm font-semibold uppercase tracking-wider"
                >
                  <MessageCircle size={16} /> Get Best Rate on WhatsApp
                </a>
              </div>

              <ul className="mt-8 grid grid-cols-2 gap-y-2 gap-x-6 text-sm text-[#2D181A]">
                {[
                  "Spot cash today",
                  "Best price guarantee",
                  "Govt-approved valuers",
                  "Decades of experience",
                  "Most trustworthy dealer",
                  "Home visits in DLF & GCR",
                ].map((x) => (
                  <li key={x} className="flex items-start gap-2">
                    <CheckCircle2
                      size={16}
                      className="mt-0.5 text-[#641C24] shrink-0"
                    />
                    {x}
                  </li>
                ))}
              </ul>
            </div>

            {/* Visual */}
            <div className="lg:col-span-5 flex items-center justify-center">
              <div className="relative w-full max-w-md">
                <img
                  src={IMAGES.ornament1}
                  alt="Cash for gold Golf Course Road Gurgaon — best rate at Top Rate"
                  loading="eager"
                  className="w-full h-72 md:h-80 object-cover shadow-[0_18px_44px_-14px_rgba(45,24,26,0.4)]"
                />
                <img
                  src={IMAGES.rupees500}
                  alt="₹500 notes spot cash payout for gold Golf Course Road"
                  loading="eager"
                  className="absolute -bottom-8 -right-4 w-3/5 h-auto object-contain shadow-[0_16px_40px_-12px_rgba(45,24,26,0.5)] rotate-[6deg]"
                />
                <img
                  src={IMAGES.rupees500}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute -bottom-4 -left-3 w-2/5 h-auto object-contain shadow-[0_16px_40px_-12px_rgba(45,24,26,0.4)] -rotate-[6deg] hidden sm:block"
                />
                <div className="absolute -top-5 -left-3 bg-[#641C24] text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.24em] px-4 py-2 shadow-lg">
                  Best Price ✦
                </div>
                <div className="absolute top-4 -right-3 bg-[#D4AF37] text-[#2D181A] text-[10px] font-bold uppercase tracking-[0.24em] px-4 py-2 shadow-lg">
                  Spot Cash Today
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="gold"
        tagline={
          <>
            Cash for Gold · Golf Course Road · Gurgaon IT Hub · DLF · Sushant
            Lok · All South Gurgaon
          </>
        }
      />

      {/* GOLD AS SECURED INVESTMENT */}
      <section
        data-testid="gold-investment-section"
        className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
              Gold — Secured for Generations · सोना एक सुरक्षित निवेश
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">
              Gold has been an{" "}
              <span className="italic text-[#641C24]">invested and secured</span>{" "}
              asset for many years.
            </h2>
          </div>
          <div className="lg:col-span-4 text-[#4A3B3C] text-base leading-relaxed">
            When a financial emergency arrives, the gold sitting in your
            locker is the fastest, most dignified source of cash — with real
            financial &amp; practical benefits over any bank loan.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {/* Benefit 1: Rapid Processing */}
          <div className="bg-[#FAF6EC] border border-[#E5DCC5] p-8 md:p-10 shadow-sm">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
              <span className="text-lg leading-none">⚡</span>
              Benefit 01
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold mt-4 leading-tight">
              Rapid Processing &amp; Instant Liquidity
            </h3>
            <ul className="mt-6 space-y-4 text-sm text-[#2D181A]">
              <li className="flex items-start gap-3">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 text-[#641C24] shrink-0"
                />
                <span>
                  <span className="font-semibold">Immediate payout:</span>{" "}
                  Cash on the spot when you sell your gold, or a disbursed gold
                  loan handed to you in minutes.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 text-[#641C24] shrink-0"
                />
                <span>
                  <span className="font-semibold">
                    No processing delays:
                  </span>{" "}
                  Traditional bank loans require days or weeks for background
                  verification and paperwork.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 text-[#641C24] shrink-0"
                />
                <span>
                  <span className="font-semibold">
                    No end-use restrictions:
                  </span>{" "}
                  Use the cash immediately for any emergency — medical bills,
                  fees, business working capital, anything.
                </span>
              </li>
            </ul>
          </div>

          {/* Benefit 2: Zero Credit Requirements */}
          <div className="bg-[#2D181A] text-[#FFFDF8] p-8 md:p-10 border-l-4 border-[#D4AF37]">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              <span className="text-lg leading-none">📄</span>
              Benefit 02
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold mt-4 leading-tight text-[#FFFDF8]">
              Zero Credit Score or Income Requirements
            </h3>
            <ul className="mt-6 space-y-4 text-sm text-[#FFFDF8]/90">
              <li className="flex items-start gap-3">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 text-[#D4AF37] shrink-0"
                />
                <span>
                  <span className="font-semibold text-[#FFFDF8]">
                    No credit checks:
                  </span>{" "}
                  Lenders evaluate the value of the physical asset, completely
                  ignoring your borrowing history or low credit scores.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 text-[#D4AF37] shrink-0"
                />
                <span>
                  <span className="font-semibold text-[#FFFDF8]">
                    No proof of income:
                  </span>{" "}
                  No salary slips, tax returns, or employment verification
                  needed.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 text-[#D4AF37] shrink-0"
                />
                <span>
                  <span className="font-semibold text-[#FFFDF8]">
                    Universal accessibility:
                  </span>{" "}
                  Highly accessible for freelancers, students, homemakers, and
                  small business owners — everyone has equal access to their
                  own gold's value.
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 md:mt-14 bg-[#D4AF37] text-[#2D181A] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold">
              The Top Rate Advantage
            </div>
            <div className="font-display text-xl md:text-2xl font-bold mt-1 leading-tight">
              Unlock your gold's value — same day, no questions on credit,
              transparent formula.
            </div>
          </div>
          <a
            href={telLinkPrimary}
            className="inline-flex items-center gap-2 bg-[#641C24] text-[#FFFDF8] hover:bg-[#4A151A] px-6 py-3.5 text-sm font-semibold uppercase tracking-wider shrink-0"
          >
            <Phone size={16} /> {BRAND.phonePrimary}
          </a>
        </div>
      </section>

      {/* REASONS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
          Why Top Rate is the Most Trusted in South Gurgaon
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] max-w-3xl">
          Six reasons families near Golf Course Road{" "}
          <span className="italic text-[#641C24]">choose Top Rate</span>.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-12">
          {REASONS.map((r) => (
            <div
              key={r.title}
              className="bg-white border border-[#E5DCC5] p-8 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="w-11 h-11 border border-[#D4AF37] flex items-center justify-center text-[#641C24]">
                <r.icon size={20} strokeWidth={1.6} />
              </div>
              <h3 className="font-display text-xl font-bold mt-5">
                {r.title}
              </h3>
              <p className="text-sm text-[#4A3B3C] leading-relaxed mt-3">
                {r.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <BrandStamp
        variant="dark"
        tagline={
          <>Top Rate · Trustworthy · Govt-Approved · Decades of Experience</>
        }
      />

      {/* AREAS SERVED */}
      <section className="bg-[#FAF6EC] tr-grain border-y border-[#E5DCC5]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
            <div className="lg:col-span-8">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
                Areas We Serve
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">
                <span className="italic text-[#641C24]">Local dealer</span> —
                right next to Golf Course Road.
              </h2>
            </div>
            <div className="lg:col-span-4 text-[#4A3B3C] text-base leading-relaxed">
              Our Sapphire Mall Sector 49 branch is a short drive from every
              DLF phase. Home visits available across all listed areas.
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {NEARBY.map((a) => (
              <span
                key={a}
                className="inline-flex items-center gap-1.5 bg-white border border-[#E5DCC5] text-[#2D181A] px-4 py-2 text-xs uppercase tracking-[0.14em] font-semibold"
              >
                <MapPin size={11} className="text-[#D4AF37]" /> {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      <BrandStamp
        variant="light"
        tagline={<>Best Rate Today · Just Call · Spot Cash</>}
      />

      {/* FAQ */}
      <section className="bg-[#2D181A] text-[#FFFDF8]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Frequently Asked Questions
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] max-w-3xl">
            Selling gold near{" "}
            <span className="italic text-[#D4AF37]">Golf Course Road</span> —
            everything you need to know.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            {FAQ.map((f, i) => (
              <div key={i} className="border-l-2 border-[#D4AF37]/60 pl-6">
                <h3 className="font-display text-lg lg:text-xl font-bold text-[#FFFDF8]">
                  {f.q}
                </h3>
                <p className="text-sm text-[#FFFDF8]/80 leading-relaxed mt-3">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="bg-[#641C24] text-[#FFFDF8] p-8 md:p-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.05]">
                Just call.{" "}
                <span className="italic text-[#D4AF37]">Best price today.</span>
              </h2>
              <p className="text-[#FFFDF8]/80 mt-4 leading-relaxed">
                Best price. Best rate. Spot cash. That's the Top Rate promise.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <a
                href={telLinkPrimary}
                data-testid="gcr-cta-call"
                className="flex items-center justify-between gap-3 bg-[#D4AF37] hover:bg-[#B8860B] text-[#2D181A] px-6 py-4"
              >
                <span className="inline-flex items-center gap-2">
                  <Phone size={16} strokeWidth={2.2} />
                  <span className="text-xs uppercase tracking-[0.22em] font-semibold">
                    Call
                  </span>
                </span>
                <span className="font-display font-black text-xl md:text-2xl tracking-tight">
                  {BRAND.phonePrimary}
                </span>
              </a>
              <a
                href={waLink(
                  "Namaste! I'm from Golf Course Road / DLF area. Please share today's best gold rate. (Top Rate)"
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-3 bg-[#25D366] hover:bg-[#1FBA57] text-white px-6 py-4"
              >
                <span className="inline-flex items-center gap-2">
                  <MessageCircle size={16} strokeWidth={2.2} />
                  <span className="text-xs uppercase tracking-[0.22em] font-semibold">
                    WhatsApp
                  </span>
                </span>
                <span className="font-display font-black text-xl md:text-2xl tracking-tight">
                  Chat Now
                </span>
              </a>
              <Link
                to="/branches"
                className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#D4AF37] hover:text-[#FFFDF8] mt-1"
              >
                → Find Nearest Branch
              </Link>
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="dark"
        tagline={
          <>Top Rate · Sona Do Paisa Lo · Most Trusted Gold Dealer in Gurgaon</>
        }
      />
    </div>
  );
}
